<template>
  <div class="reset-page">
    <div class="reset-bg-pattern"></div>
    <div class="reset-container fade-in">
      <div class="reset-header">
        <img class="logotipo" src="/logo-white.png" alt="logo">
      </div>
      
      <div class="reset-card">
        <!-- Form State -->
        <div v-if="!changeRequested">
          <div class="reset-title">
            <h2>Redefinir senha</h2>
            <p>Defina uma nova senha para a sua conta</p>
          </div>
          
          <form @submit.prevent="resetPassword">
            <div class="field">
              <label class="label">Nova senha</label>
              <div class="control has-icons-left has-icons-right">
                <input 
                  required 
                  class="input" 
                  :class="{'is-success': enoughPasswordChars}" 
                  :type="showPassword ? 'text' : 'password'" 
                  v-model="password"
                >
                <span class="icon is-small is-left">
                  <i class="fas fa-lock"></i>
                </span>
                <span 
                  class="icon is-small is-right password-toggle"
                  @click="showPassword = !showPassword"
                >
                  <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                </span>
              </div>
              <PasswordForceCheck :password="password"></PasswordForceCheck>
            </div>
            
            <div class="field">
              <label class="label">Confirmar nova senha</label>
              <div class="control has-icons-left">
                <input 
                  required 
                  class="input" 
                  :class="{'is-danger': passwordFilled && !samePassword}" 
                  type="password" 
                  v-model="passwordConfirmation"
                >
                <span class="icon is-small is-left">
                  <i class="fas fa-lock"></i>
                </span>
              </div>
              <p class="help is-danger" v-if="passwordFilled && !samePassword">
                As senhas não coincidem
              </p>
            </div>

            <button 
              :disabled="!validPassword" 
              class="button is-primary is-fullwidth" 
              :class="{'is-loading':isRequesting}"
            >
              <i class="fas fa-key mr-2"></i>
              Alterar senha
            </button>
          </form>
        </div>
        
        <!-- Success State -->
        <div v-else-if="isChanged" class="success-state">
          <div class="success-icon">
            <i class="fas fa-check"></i>
          </div>
          <h2>Senha alterada!</h2>
          <p>A sua senha foi redefinida com sucesso.</p>
          <router-link class="button is-link mt-4" to="/login">
            <i class="fas fa-sign-in-alt mr-2"></i>
            Entrar com nova senha
          </router-link>
        </div>
        
        <!-- Error State -->
        <div v-else class="error-state">
          <div class="error-icon">
            <i class="fas fa-times"></i>
          </div>
          <h2>Erro</h2>
          <p>Não foi possível alterar a senha. O link pode ter expirado.</p>
          <router-link class="button is-primary mt-4" to="/login">
            <i class="fas fa-arrow-left mr-2"></i>
            Voltar ao login
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PasswordForceCheck from "@/components/PasswordForceCheck.vue";

export default {
  name: "ResetPassword",
  components: {PasswordForceCheck},
  data() {
    return {
      password: '',
      passwordConfirmation: '',
      changeRequested: false,
      isRequesting: false,
      isChanged: false,
      showPassword: false,
    }
  },
  computed: {
    passwordFilled() {
      return (this.password.length > 0)
    },
    enoughPasswordChars() {
      return (this.password.length >= 8)
    },
    samePassword() {
      return (this.password === this.passwordConfirmation)
    },
    validPassword() {
      return (this.password.length >= 8 && this.password === this.passwordConfirmation)
    }
  },
  methods: {
    resetPassword() {
      if (this.validPassword) {
        this.isRequesting = true;
        this.$store.dispatch('setNewPasswordFromRecovery', {
          recoverHash: this.$route.params['hash'],
          newPassword: this.password
        }).then(() => {
          this.isRequesting = false;
          this.changeRequested = true;
          this.isChanged = true;
        }).catch(() => {
          this.isRequesting = false;
          this.changeRequested = true;
          this.isChanged = false;
        })
      }
    }
  }
}
</script>

<style scoped>
.reset-page {
  width: 100vw;
  height: 100vh;
  background: linear-gradient(135deg, #0755A0 0%, #0F172A 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.reset-bg-pattern {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 1px, transparent 1px),
    radial-gradient(circle at 75% 75%, rgba(255,255,255,0.05) 1px, transparent 1px);
  background-size: 50px 50px;
  pointer-events: none;
}

.reset-container {
  width: 100%;
  max-width: 420px;
  padding: 20px;
  position: relative;
  z-index: 1;
}

.reset-header {
  text-align: center;
  margin-bottom: 2rem;
}

.reset-header .logotipo {
  width: 180px;
}

.reset-card {
  background: var(--surface);
  border-radius: var(--radius-xl);
  padding: 2.5rem;
  box-shadow: var(--shadow-xl);
}

.reset-title {
  text-align: center;
  margin-bottom: 2rem;
}

.reset-title h2 {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
}

.reset-title p {
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.field {
  margin-bottom: 1.25rem;
}

.reset-card .label {
  font-weight: 600;
  font-size: 0.875rem;
}

.reset-card .input {
  height: 48px;
  font-size: 1rem;
  padding-left: 2.5rem;
}

.reset-card .input:focus {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(7, 85, 160, 0.15);
}

.reset-card .input.is-success {
  border-color: var(--success);
}

.reset-card .icon {
  height: 48px;
  line-height: 48px;
}

.password-toggle {
  cursor: pointer;
}

.password-toggle:hover {
  color: var(--primary);
}

.reset-card .button.is-primary {
  height: 48px;
  font-size: 1rem;
  font-weight: 600;
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
  border: none;
  margin-top: 1rem;
}

.reset-card .button.is-primary:hover {
  background: linear-gradient(135deg, var(--primary-dark) 0%, #042C57 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(7, 85, 160, 0.3);
}

.reset-card .button.is-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.success-state, .error-state {
  text-align: center;
  padding: 1rem 0;
}

.success-icon, .error-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 20px;
  border-radius: var(--radius-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

.success-icon {
  background: linear-gradient(135deg, var(--success) 0%, #059669 100%);
  color: white;
}

.error-icon {
  background: linear-gradient(135deg, var(--danger) 0%, #DC2626 100%);
  color: white;
}

.success-state h2, .error-state h2 {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 8px;
}

.success-state p, .error-state p {
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.success-state .button, .error-state .button {
  height: 48px;
  padding: 0 32px;
  font-size: 1rem;
  font-weight: 600;
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
  border: none;
}

.success-state .button:hover, .error-state .button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(7, 85, 160, 0.3);
}
</style>
